import { useState, useEffect, createContext } from "react";

import { userApi } from "../api";
import { User } from "../api/interfaces";
import { LoginRequestResult } from "../api/types/user";
import IAuthContext from "./interfaces/auth";

export const AuthContext = createContext<IAuthContext>({} as IAuthContext);

const AuthProvider: React.FC = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLogged, setIsLogged] = useState<boolean>(false);

  const setCurrentUser = async () => {
    await userApi.fetchCurrentUser().then(async (data) => {
      setUser(data.params);
    });
  };

  useEffect(() => {
    initSession();
  }, []);

  const login = async (email: string, password: string) => {
    const payload = {
      email: email,
      password: password,
    };

    const loginRes = await userApi.logInUser(payload);

    if (loginRes.status < 400) {
      if (loginRes.params.user.role === 1) {
        await setCurrentUser();
        
        storeTokens(loginRes.params);
        setIsLogged(true);
      } else {
        setIsLogged(false);
        const error = "Insufficient rights";
        
        return { error };
      }
    } else {
      setIsLogged(false);
      return loginRes.params;
    }
  };

  const initSession = async () => {
    const expiresAt = sessionStorage.getItem("exp");
    if (expiresAt) {
      const isTokenValid = Date.now() < new Date(expiresAt).getTime();
      if (isTokenValid) {
        await setCurrentUser();
        setIsLogged(true);
        return;
      }
    }
    const refreshToken = sessionStorage.getItem("rt");
    if (refreshToken) {
      const newTokenRes = await userApi.refreshSession({ refreshToken });
      const { status, params } = newTokenRes;
      if (status < 400) {
        await setCurrentUser();
        sessionStorage.setItem("t", params.token);
        sessionStorage.setItem("exp", params.expiresIn.toString());

        setIsLogged(true);
        return;
      }
    }

    setIsLogged(false);
    return;
  };

  const logout = async () => {
    setUser(null);
    setIsLogged(false);
    sessionStorage.clear();

    window.location.reload();
  };

  const storeTokens = (payload: LoginRequestResult) => {
    sessionStorage.setItem("t", payload.token);
    sessionStorage.setItem("exp", payload.expiresIn.toString());
    sessionStorage.setItem("rt", payload.refreshToken);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isLogged,
        setUser,
        login,
        logout,
        initSession,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;
